import React, { useState, useEffect } from 'react';
import {
  getSettingsApi,
  updateSettingsApi,
  getBrandsApi,
  uploadBrandLogoApi,
  getCohortsApi,
  createCohortApi,
  updateCohortApi,
  deleteCohortApi,
  uploadCohortsCsvApi,
  getSlotsApi,
  createSlotApi,
  updateSlotApi,
  deleteSlotApi
} from '../../utils/api';
import { formatNumber, fileToBase64 } from '../../utils/helpers';
import { Settings as SettingsIcon, Upload, Plus, Trash2, FileText } from 'lucide-react';
import CohortManager from '../../components/CohortManager';
import SalesTrends from '../../components/SalesTrends';

const BackOffice = ({ showToast }) => {
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    app_live: true,
    app_name: 'STREAM',
    max_slots_per_week_per_brand: 3,
    allow_brand_metrics: true,
    allow_self_request: true,
    best_time_start: '19:00',
    best_time_end: '22:00',
    best_days_order: ['Tuesday', 'Thursday', 'Wednesday', 'Friday', 'Monday', 'Saturday', 'Sunday']
  });

  // CSV upload
  const [csvContent, setCsvContent] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, brandsRes, cohortsRes, slotsRes] = await Promise.all([
        getSettingsApi(),
        getBrandsApi(),
        getCohortsApi(),
        getSlotsApi()
      ]);
      
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
      setBrands(brandsRes.data);
      setCohorts(cohortsRes.data);
      setSlots(slotsRes.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSettingsApi(settingsForm);
      showToast('Settings updated successfully', 'success');
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update settings', 'error');
    }
  };

  const handleBrandLogoUpload = async (brandId, file) => {
    try {
      const base64 = await fileToBase64(file);
      await uploadBrandLogoApi(brandId, base64);
      showToast('Brand logo uploaded successfully', 'success');
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to upload logo', 'error');
    }
  };

  const handleCohortAdd = async (cohortData) => {
    await createCohortApi(cohortData);
    loadData();
  };

  const handleCohortEdit = async (cohortId, updateData) => {
    await updateCohortApi(cohortId, updateData);
    loadData();
  };

  const handleCohortDelete = async (cohortId) => {
    await deleteCohortApi(cohortId);
    loadData();
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!csvContent) {
      showToast('Please enter CSV content', 'error');
      return;
    }

    try {
      const result = await uploadCohortsCsvApi(csvContent);
      showToast(
        `${result.data.cohorts_added} cohorts added. ${result.data.errors.length > 0 ? `Errors: ${result.data.errors.length}` : ''}`,
        result.data.errors.length > 0 ? 'warning' : 'success'
      );
      setCsvContent('');
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to upload CSV', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Control Panel</h1>
        <p className="text-slate-400">System settings, brand configuration, and cohort management</p>
      </div>

      <div className="space-y-6">
        {/* Global Settings */}
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <SettingsIcon className="text-indigo-400" size={24} />
            <h2 className="text-xl font-semibold text-white">Global Settings</h2>
          </div>
          
          <form onSubmit={handleSettingsSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  App Name
                </label>
                <input
                  type="text"
                  value={settingsForm.app_name}
                  onChange={(e) => setSettingsForm({ ...settingsForm, app_name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Max Slots Per Week (Per Brand)
                </label>
                <input
                  type="number"
                  min="1"
                  value={settingsForm.max_slots_per_week_per_brand}
                  onChange={(e) => setSettingsForm({ ...settingsForm, max_slots_per_week_per_brand: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-3 p-4 bg-slate-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.app_live}
                  onChange={(e) => setSettingsForm({ ...settingsForm, app_live: e.target.checked })}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                />
                <div>
                  <p className="text-sm font-medium text-white">App Live</p>
                  <p className="text-xs text-slate-500">Enable/disable the app</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-4 bg-slate-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.allow_brand_metrics}
                  onChange={(e) => setSettingsForm({ ...settingsForm, allow_brand_metrics: e.target.checked })}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                />
                <div>
                  <p className="text-sm font-medium text-white">Allow Brand Metrics</p>
                  <p className="text-xs text-slate-500">Brands can view metrics</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-4 bg-slate-900 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.allow_self_request}
                  onChange={(e) => setSettingsForm({ ...settingsForm, allow_self_request: e.target.checked })}
                  className="w-5 h-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
                />
                <div>
                  <p className="text-sm font-medium text-white">Allow Self Request</p>
                  <p className="text-xs text-slate-500">Brands can request slots</p>
                </div>
              </label>
            </div>
            
            {/* Analytics Settings */}
            <div className="border-t border-slate-700 pt-4 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">📈 Analytics Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Best Time Start (24-hour format)
                  </label>
                  <input
                    type="time"
                    value={settingsForm.best_time_start}
                    onChange={(e) => setSettingsForm({ ...settingsForm, best_time_start: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Best Time End (24-hour format)
                  </label>
                  <input
                    type="time"
                    value={settingsForm.best_time_end}
                    onChange={(e) => setSettingsForm({ ...settingsForm, best_time_end: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Best Days Order (Drag to reorder - Top = Best)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                    const currentOrder = settingsForm.best_days_order || [];
                    const position = currentOrder.indexOf(day) + 1;
                    
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const newOrder = [...currentOrder];
                          const index = newOrder.indexOf(day);
                          if (index > 0) {
                            // Move up
                            [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                            setSettingsForm({ ...settingsForm, best_days_order: newOrder });
                          }
                        }}
                        className="p-3 bg-slate-900 hover:bg-slate-800 rounded-lg text-center transition-colors border border-slate-700"
                      >
                        <div className="text-xs text-slate-500 mb-1">#{position}</div>
                        <div className="text-sm font-medium text-white">{day.slice(0, 3)}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">Click a day to move it up in the ranking</p>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                Save Settings
              </button>
            </div>
          </form>
        </div>

        {/* Brand Logos */}
        <div className="bg-slate-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Brand Logos</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((brand) => (
              <div key={brand.id} className="bg-slate-900 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-3 h-3 rounded-md shadow-sm"
                    style={{ backgroundColor: brand.color }}
                  />
                  <h3 className="text-white font-semibold">{brand.name}</h3>
                </div>
                
                {brand.logo && (
                  <div className="mb-3">
                    <img
                      src={brand.logo}
                      alt={`${brand.name} logo`}
                      className="w-full h-32 object-contain bg-white rounded-lg"
                    />
                  </div>
                )}
                
                <label className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg cursor-pointer transition-colors">
                  <Upload size={16} />
                  {brand.logo ? 'Change Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleBrandLogoUpload(brand.id, file);
                    }}
                    className="hidden"
                  />
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Cohort Management */}
        <div className="bg-slate-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Cohort Management</h2>
          
          <CohortManager
            cohorts={cohorts}
            brands={brands}
            onAdd={handleCohortAdd}
            onEdit={handleCohortEdit}
            onDelete={handleCohortDelete}
            showToast={showToast}
          />
        </div>

        {/* CSV Upload */}
        <div className="bg-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="text-indigo-400" size={24} />
            <h2 className="text-xl font-semibold text-white">Upload Cohorts via CSV</h2>
          </div>
          
          <p className="text-sm text-slate-400 mb-4">
            Format: cohort_name, user_count, brand_name, user_type
          </p>
          
          <form onSubmit={handleCsvUpload} className="space-y-4">
            <textarea
              value={csvContent}
              onChange={(e) => setCsvContent(e.target.value)}
              placeholder={`cohort_name,user_count,brand_name,user_type\nPremium Users,5000,Wellcore,Premium\nActive Subscribers,3000,YouWeFit,Active`}
              rows={6}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
            
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Upload size={18} />
              Upload CSV
            </button>
          </form>
        </div>

        {/* Sales Trends Analysis */}
        <div className="bg-slate-800 rounded-lg p-6">
          <SalesTrends showToast={showToast} />
        </div>
      </div>
    </div>
  );
};

export default BackOffice;
